import { useCallback, useState } from 'react';

import { logger } from '../utils/logger';
import { triggerBrowserDownload } from '../utils/pdfEditorUtils';

interface UsePDFExportHandlerParams {
  exportPDF: () => Promise<Uint8Array>;
  onDownload?: (pdfBytes: Uint8Array) => void;
}

export const usePDFExportHandler = ({
  exportPDF,
  onDownload,
}: UsePDFExportHandlerParams) => {
  const [isExportingLocal, setIsExportingLocal] = useState(false);

  const handleExport = useCallback(async () => {
    setIsExportingLocal(true);

    try {
      const pdfBytes = await exportPDF();

      if (onDownload) {
        try {
          await onDownload(pdfBytes);
          logger.info('[PDFEditor] onDownload callback succeeded');
        } catch (callbackError) {
          logger.error(
            '[PDFEditor] onDownload callback failed:',
            callbackError,
          );
          logger.info(
            'Failed to process exported PDF. Downloading to browser instead.',
          );
          triggerBrowserDownload(pdfBytes);
        }
      } else {
        triggerBrowserDownload(pdfBytes);
      }
    } catch (error) {
      logger.error('[PDFEditor] PDF export failed:', error);
      logger.info('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingLocal(false);
    }
  }, [exportPDF, onDownload]);

  return { handleExport, isExportingLocal };
};
